<script setup>
import { ref, onMounted } from 'vue';

import { useOracleHcrCertificate } from '@/composables/useOracleHcrCertificate';
import { useNotification } from '@/composables/useNotification';

import Notification from '@/components/common/SnackBar.vue';

const { notification, notify } = useNotification();
const cert = useOracleHcrCertificate();

const showActivateDialog = ref(false);
const showRollbackDialog = ref(false);
const rollbackTarget = ref(null);

async function handleGenerate() {
  await cert.generateCertificate(notify);
}

async function handleActivate() {
  showActivateDialog.value = false;
  await cert.activateCertificate(notify);
}

async function handleDownload(ohch_id) {
  await cert.downloadCert(ohch_id, notify);
}

function promptRollback(row) {
  rollbackTarget.value = row;
  showRollbackDialog.value = true;
}

async function handleRollback() {
  showRollbackDialog.value = false;
  await cert.rollbackCertificate(rollbackTarget.value.ohch_id, notify);
  rollbackTarget.value = null;
}

onMounted(() => {
  cert.fetchStatus();
});
</script>

<template>
  <div class="oracle-hcr-certificate">
    <Notification :notification="notification" />

    <!-- Environment Info -->
    <v-alert type="info" text dense class="mb-4" v-if="cert.certStatus.environment">
      <strong>Environment:</strong> {{ cert.certStatus.environment }}
      &mdash; AWS Secret: <code>oracleHCR-{{ cert.certStatus.environment }}-RSAPrivateKey</code>
    </v-alert>

    <!-- Active Certificate -->
    <v-card outlined class="mb-4 pa-4" v-if="cert.certStatus.active && cert.certStatus.active.x5t">
      <div class="d-flex align-center">
        <v-chip color="success" small class="mr-3">Active</v-chip>
        <div>
          <div><strong>x5t:</strong> {{ cert.certStatus.active.x5t }}</div>
          <div><strong>AWS Secret:</strong> {{ cert.certStatus.active.secretName }}</div>
        </div>
      </div>
    </v-card>
    <v-alert v-else type="warning" text dense class="mb-4">
      No active certificate configured.
    </v-alert>

    <!-- Pending Certificate -->
    <v-card outlined class="mb-4 pa-4" color="orange lighten-5" v-if="cert.certStatus.pending">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-chip color="orange" small dark class="mr-3">Pending</v-chip>
          <div>
            <div><strong>x5t:</strong> {{ cert.certStatus.pending.x5t }}</div>
            <div><strong>Generated:</strong> {{ cert.formatDate(cert.certStatus.pending.generatedAt) }}</div>
            <div><strong>Expires:</strong> {{ cert.formatDate(cert.certStatus.pending.expiresAt) }}</div>
          </div>
        </div>
        <v-btn color="success" @click="showActivateDialog = true" :loading="cert.activating.value">
          <v-icon left>mdi-check-circle</v-icon>
          Activate
        </v-btn>
      </div>
    </v-card>

    <!-- Generate New Certificate -->
    <v-card outlined class="mb-4 pa-4">
      <h4 class="mb-3">Generate New Certificate</h4>
      <v-row dense>
        <v-col cols="3">
          <v-text-field v-model.number="cert.form.validityDays" label="Validity (days)" outlined dense type="number" />
        </v-col>
      </v-row>
      <v-btn color="primary" @click="handleGenerate" :loading="cert.generating.value" class="mt-2">
        <v-icon left>mdi-certificate</v-icon>
        Generate &amp; Download
      </v-btn>
    </v-card>

    <!-- History -->
    <v-card outlined class="pa-4" v-if="cert.certStatus.history && cert.certStatus.history.length">
      <h4 class="mb-3">Certificate History</h4>
      <v-simple-table dense>
        <thead>
          <tr>
            <th>Status</th>
            <th>x5t Fingerprint</th>
            <th>AWS Secret</th>
            <th>Generated</th>
            <th>Activated</th>
            <th>Expires</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in cert.certStatus.history" :key="row.ohch_id">
            <td>
              <v-chip x-small :color="cert.statusColor(row.status)" :dark="row.status !== 'pending'">{{ row.status }}</v-chip>
            </td>
            <td style="font-family: monospace; font-size: 12px">{{ row.x5t }}</td>
            <td>{{ row.aws_secret_name }}</td>
            <td>{{ cert.formatDate(row.generated_at) }}</td>
            <td>{{ row.activated_at ? cert.formatDate(row.activated_at) : '-' }}</td>
            <td>{{ cert.formatDate(row.expires_at) }}</td>
            <td>
              <v-btn x-small text color="primary" @click="handleDownload(row.ohch_id)">
                <v-icon x-small left>mdi-download</v-icon>
                Download
              </v-btn>
              <v-btn v-if="row.status === 'rotated'" x-small text color="warning" @click="promptRollback(row)" :loading="cert.rollingBack.value">
                <v-icon x-small left>mdi-undo</v-icon>
                Rollback
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- Activate Confirmation Dialog -->
    <v-dialog v-model="showActivateDialog" max-width="450">
      <v-card>
        <v-card-title>Activate Pending Certificate</v-card-title>
        <v-card-text>
          This will replace the current active certificate with the pending one.
          Ensure the vendor has uploaded the public certificate on their side before proceeding.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showActivateDialog = false">Cancel</v-btn>
          <v-btn color="success" @click="handleActivate">Confirm Activate</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rollback Confirmation Dialog -->
    <v-dialog v-model="showRollbackDialog" max-width="450">
      <v-card>
        <v-card-title>Rollback Certificate</v-card-title>
        <v-card-text>
          This will restore the previously rotated certificate as the active one.
          The current active certificate will be marked as rotated.
          <div v-if="rollbackTarget" class="mt-3">
            <strong>x5t:</strong> <span style="font-family: monospace; font-size: 12px">{{ rollbackTarget.x5t }}</span>
          </div>
          Ensure the vendor has the matching public certificate before proceeding.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showRollbackDialog = false">Cancel</v-btn>
          <v-btn color="warning" @click="handleRollback">Confirm Rollback</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
</style>
